"use client";

import { FEED_API } from "@/lib/apis";
import { IGetCommunityFeedPostsResponseDTO } from "@/types/post";
import { useQuery } from "@tanstack/react-query";
import Link from "next/link";
import FeedPost from "./feed-post";
import FeedSkeletonLoading from "./feed-skeleton-loading";
import { parsePostContentJSON } from "@/lib/utils";

interface IFeedProps {
	communitySlug?: string;
	channelSlug?: string;
	isPublicFeed?: boolean;
}

// TODO (zeekhuge): Duplicate exists in src/modules/community/components/feed#24
const fetchPosts = async ({
	communitySlug,
	channelSlug,
	isPublicFeed,
}: {
	communitySlug?: string;
	channelSlug?: string;
	isPublicFeed?: boolean;
}) => {
	const url = new URL(FEED_API);
	url.searchParams.set("page", "1");
	url.searchParams.set("perPage", "5");

	if (communitySlug) {
		url.searchParams.set("community-slug", communitySlug);
	}

	if (channelSlug) {
		url.searchParams.set("channel-slug", channelSlug);
	}

	if (isPublicFeed) {
		url.searchParams.set("is-public-feed", "true");
	}

	const response = await fetch(url);
	const data = await response.json();

	if (!response.ok) {
		throw new Error(data?.message || "You do not have access to view this feed.");
	}

	return data;
};

const FeedHomePage = ({ communitySlug, channelSlug, isPublicFeed = false }: IFeedProps) => {
	const { isLoading, error, isError, data } = useQuery<IGetCommunityFeedPostsResponseDTO>({
		queryKey: ["posts", communitySlug, channelSlug],
		queryFn: () =>
			fetchPosts({
				communitySlug,
				channelSlug,
				isPublicFeed,
			}),
	});

	let message = null;

	if (isLoading) {
		message = <FeedSkeletonLoading />;
	} else if (isError) {
		message = <p className="py-6 text-center text-gray-500">{(error as Error).message}</p>;
	} else if (data?.data?.posts.length === 0) {
		message = <p className="py-6 text-center text-gray-500">No discussions found</p>;
	}

	return (
		<section>
			<div className="space-y-4">
				{message ??
					data?.data?.posts.map((post) => (
						<FeedPost key={post.id} post={parsePostContentJSON(post)!} />
					))}
			</div>
			{data?.data?.posts && data?.data?.posts.length > 0 && (
				<div className="mt-6 flex justify-center">
					<Link
						href={`/communities/feed`}
						className="rounded-lg bg-brand-primary px-6 py-2 text-sm font-medium text-white hover:bg-brand-primary/90"
					>
						Show More
					</Link>
				</div>
			)}
		</section>
	);
};

export default FeedHomePage;
